import React from 'react'
import classNames from 'classnames'
import type { AdditionalWorkOption } from './types'
import { handleKeyboardAction } from './keyboard'
import type { useAddAdditionalWorkPanelStyles } from './useAddAdditionalWorkPanelStyles'

interface AdditionalWorkOptionCardProps {
  option: AdditionalWorkOption
  selected: boolean
  onToggle: (id: string) => void
  classes: ReturnType<typeof useAddAdditionalWorkPanelStyles>
}

function formatHours(hours: number): string {
  return hours === Math.round(hours) ? `${hours}h` : `${hours} hr`
}

export default function AdditionalWorkOptionCard({
  option,
  selected,
  onToggle,
  classes
}: AdditionalWorkOptionCardProps) {
  const hasSubareas = option.subareas && option.subareas.length > 0
  const hasFallbackName = !hasSubareas && option.optionName && option.optionName !== option.areaName
  const hasBottomContent = hasSubareas || hasFallbackName || option.includesPhotos
  const showSubHours = option.hours !== undefined

  return (
    <div
      className={classes.optionCard}
      onClick={() => onToggle(option.id)}
      role="checkbox"
      aria-checked={selected}
      tabIndex={0}
      onKeyDown={(event) => handleKeyboardAction(event, () => onToggle(option.id))}
    >
      <div
        className={classNames(classes.optionCardTop, {
          [classes.optionCardTopSelected]: selected,
          [classes.optionCardTopRounded]: !hasBottomContent
        })}
      >
        <span className={classes.optionCardAreaName}>{option.areaName}</span>
        {option.hours !== undefined && !!(option.hours || option.price) && (
          <span className={classes.optionCardHours}>{formatHours(option.hours)}</span>
        )}
        {option.price !== undefined && option.hours === undefined && !!option.price && (
          <span className={classes.optionCardHours}>${option.price}</span>
        )}
      </div>
      {hasBottomContent && (
        <div className={classNames(classes.optionCardBottom, { [classes.optionCardBottomSelected]: selected })}>
          {hasSubareas
            ? option.subareas!.map((sub, i) => (
                <div key={i} className={classes.optionSubareaRow}>
                  <div className={classes.optionSubareaHeader}>
                    <span className={classes.optionSubareaName}>{sub.name}</span>
                    {showSubHours && sub.hours != null && (
                      <span className={classes.optionCardHours}>{formatHours(sub.hours)}</span>
                    )}
                  </div>
                  {sub.description && (
                    <div
                      className={classes.optionSubareaDetail}
                      dangerouslySetInnerHTML={{ __html: sub.description }}
                    />
                  )}
                  {sub.crewNote && (
                    <div className={classes.optionSubareaDetail} dangerouslySetInnerHTML={{ __html: sub.crewNote }} />
                  )}
                  {sub.coats && <div className={classes.optionSubareaDetail}>{`Coats: ${sub.coats}`}</div>}
                  {sub.reportText && (
                    <div className={classes.optionSubareaDetail} dangerouslySetInnerHTML={{ __html: sub.reportText }} />
                  )}
                </div>
              ))
            : hasFallbackName && (
                <div className={classes.optionCardOptionRow}>
                  <span
                    className={classes.optionCardOptionName}
                    dangerouslySetInnerHTML={{ __html: option.optionName }}
                  />
                </div>
              )}
        </div>
      )}
    </div>
  )
}
